import { useInterval } from "usehooks-ts";
import { GlobalStore } from "../store/global/GlobalStore";
import {
  BALANCE_REFETCH_INTERVAL_MS,
  FETCH_BALANCE_RETRY_TIMES,
  TIME_BEFORE_USER_IS_IDLE,
} from "../config/consts";
import { getBalances } from "../store/global/getBalances";
import { useCallback, useState } from "react";
import { singletonHook } from "react-singleton-hook";
import { noop } from "lodash-es";
import * as Sentry from "@sentry/browser";
import { useIdleTimer } from "react-idle-timer";
import { useTokenListByChain } from "./useTokenListByChain";

const useTimedFetchBalancesImpl = () => {
  const [failureTimes, setFailureTimes] = useState<number>(0);
  const [isFetchingBalances, setIsFetchingBalances] = useState<boolean>(false);
  const [isForceRefreshing, setIsForceRefreshing] = useState<boolean>(false);

  const [isUserIdle, setIsUserIdle] = useState<boolean>(false);
  const handleOnIdle = useCallback(() => {
    setIsUserIdle(true);
  }, []);
  const handleOnActive = useCallback(() => {
    setIsUserIdle(false);
  }, []);

  useIdleTimer({
    timeout: TIME_BEFORE_USER_IS_IDLE,
    onIdle: handleOnIdle,
    onActive: handleOnActive,
    debounce: 500,
  });

  const hasExceededAllowedTries = failureTimes >= FETCH_BALANCE_RETRY_TIMES;
  const tokenList = useTokenListByChain();
  const { address, network } = GlobalStore.useState(s => {
    return {
      address: s.address as `0x${string}`,
      network: s.network,
    };
  });

  const forceRefresh = useCallback(() => {
    setIsForceRefreshing(true);
  }, []);

  const getTimeout = (): number | null => {
    if (isForceRefreshing) {
      setTimeout(() => {
        setIsForceRefreshing(false);
      }, 2);
      return 1;
    }
    return !isUserIdle && address && !hasExceededAllowedTries ? BALANCE_REFETCH_INTERVAL_MS : null;
  };

  useInterval(
    () => {
      if (address && tokenList) {
        setIsFetchingBalances(true);
        void getBalances
          .run({ address, tokenList, chain: network })
          .then(res => {
            setIsFetchingBalances(false);
          })
          .catch(e => {
            setIsFetchingBalances(true);
            if (hasExceededAllowedTries) {
              setIsFetchingBalances(false);
              const errorMessage = `Failed to fetch balances after ${failureTimes} attempts`;
              console.error(errorMessage);
              Sentry.captureException(errorMessage, { extra: { error: e } });
            }
            setFailureTimes(prev => prev + 1);
          });
      }
    },
    // Delay in milliseconds or null to stop it
    getTimeout()
  );

  return { isFetchingBalances, forceRefresh };
};

export const useTimedFetchBalances = singletonHook(
  {
    isFetchingBalances: false,
    forceRefresh: noop,
  },
  useTimedFetchBalancesImpl
);
